<template lang="html">
  <div 
    :id="id" 
    v-scroll="syncToolbar"
    v-resize="onResize" 
    class="si-quill-editor"
    :class="{
      'rounded-corners': roundedCorners,
      'rounded-corners-top': roundedCornersTop,
      'rounded-corners-bottom': roundedCornersBottom
    }" 
    @keydown="$emit('keydown', $event)"
  >
    <quill-editor 
      ref="editor"
      v-model="content"
      :options="editorOptions"
      style="border-radius: 3px;" 
      @blur="$emit('blur')"
      @ready="onEditorReady($event)"
      v-on="$listeners"
    />
    <v-text-field
      :value="lazyValue"
      v-bind="$attrs"
      :rules="internalRules"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import uniqid from 'uniqid'
import Delta from 'quill-delta';
import Quill from 'quill';
import helpers from '@/mixins/helpers'
import binder from '@/mixins/binder'
import { storage } from '@/plugins/firebase'
import ImageResize from 'quill-image-resize-module';
import MagicUrl from 'quill-magic-url';

Quill.register('modules/imageResize', ImageResize);
Quill.register('modules/magicUrl', MagicUrl);

// var Link = Quill.import('formats/link');
// class MyLink extends Link {
//   static create(value) {
//     let node = super.create(value);
//     value = this.sanitize(value);
//     node.setAttribute('href', value);
//     if((value.startsWith('/'))) {
//       node.removeAttribute('target');
//     }
//     node.setAttribute('data-href', value);
//     return node;
//   }
// }

// Quill.register(MyLink);

// var InlineBlot = Quill.import('blots/block');
// class ImageBlot extends InlineBlot {
//   static create(data) {
//     const node = super.create(data);
//     node.setAttribute('data-src', data['data-src']);
//     node.setAttribute('data-thumb-src', data['data-thumb-src']);
//     node.setAttribute('src', data.src);
//     node.setAttribute('style', data.style || '');
//     node.setAttribute('class', data.class || '');
//     return node;
//   }
//   // static value(domNode) {
//   //   const { src, custom } = domNode.dataset;
//   //   return { src, custom };
//   // }
//   static formats(node) {
//     return { 
//       src: node.getAttribute('src'),
//       ['data-src']: node.getAttribute('data-src'),
//       ['data-thumb-src']: node.getAttribute('data-thumb-src'),
//       style: node.getAttribute('style'),
//       class: node.getAttribute('class'),
//     };
//   }
// }
// ImageBlot.blotName = 'imageBlot';
// ImageBlot.className = 'image-blot';
// ImageBlot.tagName = 'img';
// Quill.register({ 'formats/imageBlot': ImageBlot });

// https://github.com/kensnyder/quill-image-resize-module/issues/10
var BaseImageFormat = Quill.import('formats/image');
const ImageFormatAttributesList = [
    'alt',
    'height',
    'width',
    'style',
    'data-src',
    'data-thumb-src'
];

class ImageFormat extends BaseImageFormat {
  static formats(domNode) {
    return ImageFormatAttributesList.reduce(function(formats, attribute) {
      if (domNode.hasAttribute(attribute)) {
        formats[attribute] = domNode.getAttribute(attribute);
      }
      return formats;
    }, {});
  }
  format(name, value) {
    if (ImageFormatAttributesList.indexOf(name) > -1) {
      if (value) {
        this.domNode.setAttribute(name, value);
      } else {
        this.domNode.removeAttribute(name);
      }
    } else {
      super.format(name, value);
    }
  }
}

Quill.register(ImageFormat, true);

const validations = {
  isNumeric(str) {
    if (typeof str != "string") return false // we only process strings!  
    return !isNaN(str) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
          !isNaN(parseFloat(str)) // ...and ensure strings of whitespace fail
  },
  isEmail(email) {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(email);
  }
}
const Link = Quill.import('formats/link')
class linkType extends Link {
  static create (value) {
    let node = super.create(value)
    value = this.sanitize(value)
    if (validations.isNumeric(value)) {
      node.setAttribute('href', 'tel:' + value)
      node.className = 'link--tel'
    }
    if (validations.isEmail(value)) {
      node.setAttribute('href', 'mailto:' + value)
      node.className = 'link--mail'
    }
    if (value.startsWith('https://') || value.startsWith('http://')) {
      node.className = 'link--external'
    } else {
      node.removeAttribute('target')
    }
    return node
  }
}
Quill.register(linkType)

export default {
  mixins: [ helpers, binder ],
  props: {
    value: String,
    imageSavePath: String,
    roundedCorners: Boolean,
    roundedCornersTop: Boolean,
    roundedCornersBottom: Boolean,
    anchorToolbarRef: HTMLDivElement,
    required: Boolean, 
    rules: Array,
  },
  data () {
    return {
      id: uniqid(),
      anchorTop: 100,
      lazyValue: this.value,
      currentContent: null,
      checkImageTimer: null,
      toolbarTimer: null
    }
  },
  computed: {
    ...mapGetters(['_user']),
    content: {
      get () {
        return this.lazyValue
      }, 
      set (v) {
        this.lazyValue = v
        this.$emit('input', v)
      }
    },
    internalRules () {
      return this.required
       ? [v => (v || '').replace(/\s/g,'') > '' || `${this.$attrs.label || 'This field'} is required`].concat(this.rules || [])
       : this.rules || []
    },
    editorOptions () {
      return {
        modules: {
          imageResize: {},
          toolbar: {
            container: [
              [{ 'header': [1, 2, 3, false] }],
              ['bold', 'italic', 'underline'],
              // [{ 'size': ['small', false, 'large'] }],
              [{ 'indent': '-1' }, { 'indent': '+1' }],
              [{ 'align': '' }, { 'align': 'center' }, { 'align': 'right' }],
              [{ 'list': 'ordered' }, { 'list': 'bullet' }],
              ['link'],
              ...(this.imageSavePath ? [['image']] : []),
              ['clean']
            ],
            handlers: {
              image: this.imageHandler,
            },
          },
          magicUrl: true,
        },
      }
    }
  },
  watch: {
    value (v) {
      this.content = v
    },
    anchorToolbarRef (v) {
      if (v) this.$nextTick(this.onResize)
    }
  },
  mounted () {
    this.syncToolbar()
  },
  methods: {
    onResize () {
      if (this.anchorToolbarRef) {
        setTimeout(() => {
          let scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          this.anchorTop = this.anchorToolbarRef.getBoundingClientRect().top + scrollTop          
        }, 500)
      }
    },
    onEditorReady (editor) {
      // https://github.com/quilljs/quill/issues/2041
      editor.on('text-change', (delta, oldContents, source) => {
        if (source !== 'user') return;
        // use timer so that each key stroke is not running all this code
        if (this.checkImageTimer) window.clearTimeout(this.checkImageTimer)
        this.checkImageTimer = setTimeout(() => {    
          // const inserted = getImgUrls(delta);
          // Check for deleted images and delete from storage if have been deleted from editor
          const deleted = getImgUrls(editor.getContents().diff(oldContents))
          deleted.filter(d => !d).forEach(this.deleteImage)

          // check if any iamges have been pasted in. They will have data:image in src. 
          // Upload to storage and update editor
          delta.ops.forEach(op => {
            if (op.insert && op.insert.image && op.insert.image.startsWith('data:image')) {
              let dataUrl = op.insert.image
              fetch(dataUrl)
              .then(res => res.blob())
              .then(blob => this.handleImageAdd(blob, dataUrl, true))
            }
          })
        }, 500)
      });

      function getImgUrls(delta) {
        return delta.ops.filter(i => i.insert && i.insert.image).map(i => i.insert.image);
      }      
    },
    uploadFile(file, id) {
      return new Promise((resolve, reject) => {
      
      const path = `${this.imageSavePath}/${id}`
      
      var uploadTask = storage.ref(path).put(file, { id })
  
      uploadTask.on('state_changed',
        (snapshot) => console.log('Upload is ' + (snapshot.bytesTransferred / snapshot.totalBytes) * 100 + '% done'), 
        (error) => reject(error), 
        () => {
          uploadTask.snapshot.ref.getDownloadURL()
            .then(downloadUrl => {
              resolve({
                src: downloadUrl,
                thumbSrc: this._getThumbSrc(downloadUrl)
              })
            })
        }
        )
    })
    },
    async preLoadImage (src) {
      return new Promise((resolve, reject) => {
        let img = new Image()
        img.onload = resolve
        img.onerror = reject
        img.src = src
      })
    },
    async deleteImage (uri) {
      try {
        let imagePath = decodeURIComponent(uri).split('appspot.com/o/')[1].split('?')[0]
        storage.ref(imagePath).delete()
      } catch (error) {console.error(error)}
      },
    async handleImageAdd (file, dataUrl, hasPlaceholder = false) {
      const id = `image_${uniqid()}`
      try {
        const validImageTypes = ['image/gif', 'image/jpeg', 'image/png', 'image/svg+xml']
          
          if (!validImageTypes.includes(file.type)) return 
          
          if (!this.imageSavePath) return
          
          this.loadingImages = { ...this.loadingImages, [id]: true}
    
   
            const quill = this.$refs.editor.quill
            const { index } = quill.getSelection(true);
            let srcUpdated = false
            // insert the image as a placeholder with the dataUrl
            if (!hasPlaceholder) quill.insertEmbed(index, 'image', dataUrl); 

            // https://codepen.io/DmitrySkripkin/pen/pVGmPP
            // https://github.com/quilljs/quill/issues/1345#issuecomment-283907975
            // quill.insertEmbed(index, 'imageBlot', { ...defulatImage, src: dataUrl }); 
    
            // Move cursor to right side of image (easier to continue typing)
            quill.setSelection(index + 1);        
            
            // upload the file and get the downloadUrl
            const { src, thumbSrc } = await this.uploadFile(file, id)

            // preload the image before swapping. This allows the browser to cache and user does not see load lag
            await this.preLoadImage(src)
    
            // get the editor content
            let ops = quill.getContents().ops

            // map the ops to find the image with the dataUrl just set. If it exists replace with the downloaded url
            ops = ops.map(op => {
              if (op.insert && op.insert.image) {
                let image = op.insert.image
                if (image === dataUrl) {
                  op = {
                    insert: { image },
                    attributes: {
                      ...op.attributes,
                      ['data-src']: src, 
                      ['data-thumb-src']: thumbSrc
                    }
                  }
                  image = thumbSrc
                  srcUpdated = true
                }
              }
              return op
            })
            // create a new delta based on modified content ops
            let delta = new Delta(ops)
    
            // get the cursor position so we reset and not interupat the user
            const { index: cursor } = quill.getSelection(true) || {}
    
            // set the content
            quill.setContents(delta)
            this.$emit('image-saved')
                  
            // onChange(quill.root.innerHTML)
            // Set the cursor so the user is not interupted
            if (cursor) quill.setSelection(cursor);
    
            if (!srcUpdated) {
              console.log('going to delete');
              this.deleteImage(src)
            }
            this.loadingImages = { ...this.loadingImages, [id]: false}          
      } catch (error) {
        this.loadingImages = { ...this.loadingImages, [id]: false}    
      }
    },
    async imageHandler () {
       const id = `image_${uniqid()}`
      try {
        const validImageTypes = ['image/gif', 'image/jpeg', 'image/png', 'image/svg+xml'];
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.click();
        input.onchange = async () => {
          const file = input.files[0];
          
          if (!validImageTypes.includes(file.type)) return 
          
          if (!this.imageSavePath) return
          
          this.loadingImages = { ...this.loadingImages, [id]: true}
          
          const reader = new FileReader();
          
          reader.addEventListener("load", async () => {
            this.handleImageAdd(file, reader.result)
          }, false);
    
          reader.readAsDataURL(file)
          
        }
      } catch (error) {
        this.loadingImages = { ...this.loadingImages, [id]: false}    
      }
    },
    syncToolbar () {        
      if (this.toolbarTimer) window.clearTimeout(this.toolbartoolbarTimer)
      this.toolbarTimer = setTimeout(() => {      
        if (this.anchorToolbarRef) {
          const el = document.getElementById(this.id)
          const toolbarEl = el.getElementsByClassName(['ql-toolbar'])[0]
          const containerEl = el.getElementsByClassName(['ql-editor'])[0]
          const { y: editorTopPos } = el.getBoundingClientRect()
          const { height: toolbarHeight } = toolbarEl.getBoundingClientRect()
          const { left, width, top, height } = containerEl.getBoundingClientRect()

          if (
            toolbarEl 
            && this.anchorTop > editorTopPos
            && height + top > this.anchorTop  + toolbarHeight + 100
            ) {
            toolbarEl.setAttribute( 'style', `
              position: fixed; 
              top: ${this.anchorTop}px;
              left: ${left}px;
              width: ${width}px;
              z-index: 99999;
              box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
              align-self: stretch
              `)
          } else if (toolbarEl) {
            toolbarEl.setAttribute('style', `
              margin-top: -${toolbarHeight}px
            `)
            el.setAttribute('style', `
              padding-top: ${toolbarHeight}px
            `)
          }        
        }
      }, 5)

    }
  }
}
</script>

<style lang="scss">
.si-quill-editor {
  .quill-editor {
    width: 100%;
  }
  
  .ql-container {
    font-size: inherit
  }
  .v-text-field:not(.v-input--has-state)>.v-input__control>.v-input__slot:before {
    border: none
  }
  .v-text-field__slot{
    display: none !important;
  }

  .v-input{
    padding: 0;
    margin: 0;
    margin-top: -1px;
  }

  .ql-toolbar.ql-snow {
    padding: 1rem 0;
    background-color: white;
  }

  &.rounded-corners {
    .ql-toolbar {
      border-top-right-radius: inherit;
      border-top-left-radius: inherit;
    }
    .ql-container {
      border-bottom-right-radius: inherit;
      border-bottom-left-radius: inherit;
    }
  }
  &.rounded-corners-top {
    .ql-toolbar {
      border-top-right-radius: inherit;
      border-top-left-radius: inherit;
    }
  }
  &.rounded-corners-bottom {
    .ql-container {
      border-bottom-right-radius: inherit;
      border-bottom-left-radius: inherit;
    }
  }
}
</style>